/**
 * Manage Partners Screen
 * SUPER_ADMIN screen to manage sales partners (Register, List, Search, Delete)
 * Uses /api/partners endpoints
 */

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput, ActivityIndicator } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../../packages/core/src/api/client';
import { createLogger } from '../../../../packages/core/src/config/environment';
import { configurationService } from '../../../../packages/core/src/api/configuration';

const logger = createLogger('ManagePartners');

// Indian States
const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Puducherry'
];

// Major Districts by State
const DISTRICTS_BY_STATE: Record<string, string[]> = {
  'Maharashtra': ['Pune', 'Mumbai', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur'],
  'Karnataka': ['Bangalore', 'Mysore', 'Hubli', 'Belgaum', 'Mangalore', 'Gulbarga'],
  'Delhi': ['Central Delhi', 'East Delhi', 'New Delhi', 'North Delhi', 'South Delhi', 'West Delhi'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Meerut', 'Allahabad', 'Noida'],
  'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar'],
  'Rajasthan': ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Ajmer', 'Bikaner'],
  'West Bengal': ['Kolkata', 'Howrah', 'Darjeeling', 'Siliguri', 'Durgapur'],
  'Kerala': ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kollam'],
  'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda'],
  'Haryana': ['Gurgaon', 'Faridabad', 'Panipat', 'Ambala', 'Karnal'],
  'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Darbhanga'],
  'Madhya Pradesh': [
    'Agar Malwa', 'Alirajpur', 'Anuppur', 'Ashoknagar', 'Balaghat', 'Barwani',
    'Betul', 'Bhind', 'Bhopal', 'Burhanpur', 'Chhatarpur', 'Chhindwara',
    'Damoh', 'Datia', 'Dewas', 'Dhar', 'Dindori', 'Guna', 'Gwalior',
    'Harda', 'Hoshangabad', 'Indore', 'Jabalpur', 'Jhabua', 'Katni',
    'Khandwa', 'Khargone', 'Mandla', 'Mandsaur', 'Morena', 'Narsinghpur',
    'Neemuch', 'Niwari', 'Panna', 'Raisen', 'Rajgarh', 'Ratlam', 'Rewa',
    'Sagar', 'Satna', 'Sehore', 'Seoni', 'Shahdol', 'Shajapur', 'Sheopur',
    'Shivpuri', 'Sidhi', 'Singrauli', 'Tikamgarh', 'Ujjain', 'Umaria', 'Vidisha'
  ],
};

interface Partner {
  id: string;
  partnerId?: string;
  name: string;
  email: string;
  mobileCountryCode: string;
  mobileNumber: string;
  address?: string;
  city?: string;
  district?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  status?: string;
  isActive?: boolean;
  createdAt?: string;
}

interface Country {
  id: string;
  countryName: string;
  countryCode: string;
  mobileCountryCode: string;
  currency: string;
  currencySymbol: string;
}

export const ManagePartners: React.FC = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Reset password state
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({ newPassword: '', confirmPassword: '' });
  const [resetPasswordErrors, setResetPasswordErrors] = useState<Record<string, string>>({});
  const [resettingPassword, setResettingPassword] = useState(false);

  // Form state - aligned with PartnerRegistrationRequest schema
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobileCountryCode: '91',
    mobileNumber: '',
    password: '',
    confirmPassword: '',
    address: '',
    city: '',
    district: '',
    state: '',
    country: 'India',
    postalCode: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Load countries and partners on mount
  useEffect(() => {
    loadCountries();
    loadPartners();
  }, []);

  const loadCountries = async () => {
    try {
      logger.info('📋 Loading countries...');
      const response = await configurationService.getCountries({ activeOnly: true });
      setCountries(response);
      logger.info('✅ Countries loaded:', response.length);
    } catch (error) {
      logger.error('Failed to load countries', error);
    }
  };

  const loadPartners = async () => {
    try {
      setLoading(true);
      logger.info('📋 Loading partners via GET /api/partners...');

      const response = await apiClient.get<any>('/api/partners');

      if (response.status === 200 && response.data) {
        const responseData = response.data;
        // API response: { success, message, data: Partner[], count }
        const partnerList = responseData.data || responseData || [];
        setPartners(Array.isArray(partnerList) ? partnerList : []);
        logger.info(`✅ Partners loaded: ${Array.isArray(partnerList) ? partnerList.length : 0}`);
      }
    } catch (error: any) {
      logger.error('❌ Failed to load partners', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load partners';
      window.alert('Error: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const searchPartners = async (query: string) => {
    if (!query.trim()) {
      loadPartners();
      return;
    }
    try {
      setLoading(true);
      logger.info(`🔍 Searching partners: "${query}"`);

      const response = await apiClient.get<any>(`/api/partners/search?q=${encodeURIComponent(query)}`);

      if (response.status === 200 && response.data) {
        const responseData = response.data;
        const partnerList = responseData.data || responseData || [];
        setPartners(Array.isArray(partnerList) ? partnerList : []);
        logger.info(`✅ Search results: ${Array.isArray(partnerList) ? partnerList.length : 0}`);
      }
    } catch (error: any) {
      logger.error('❌ Search failed', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = useCallback((text: string) => {
    setSearchQuery(text);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      searchPartners(text);
    }, 500);
    setSearchTimeout(timeout);
  }, [searchTimeout]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    const cleanMobile = formData.mobileNumber.trim().replace(/[\s\-()]/g, '');
    if (!cleanMobile) {
      errors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{7,15}$/.test(cleanMobile)) {
      errors.mobileNumber = 'Mobile number must be 7-15 digits';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.state) {
      errors.state = 'State is required';
    }

    if (!formData.district) {
      errors.district = 'District is required';
    }

    setFormErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    console.log('📋 [ManagePartners] Validation result:', isValid ? '✅ PASSED' : '❌ FAILED', errors);
    return isValid;
  };

  const handleAddPartner = async () => {
    console.log('🔵 [ManagePartners] Register Partner button clicked');
    logger.info('🔵 Register Partner button clicked, validating form...');

    if (!validateForm()) {
      console.log('❌ [ManagePartners] Form validation failed');
      logger.info('❌ Form validation failed');
      return;
    }

    console.log('✅ [ManagePartners] Form validation passed, calling API...');

    try {
      setLoading(true);
      logger.info('📤 Registering partner via POST /api/partners/register');

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        mobileCountryCode: formData.mobileCountryCode,
        mobileNumber: formData.mobileNumber.trim().replace(/[\s\-()]/g, ''),
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        address: formData.address.trim(),
        city: formData.city.trim(),
        district: formData.district,
        state: formData.state,
        country: formData.country,
        postalCode: formData.postalCode.trim(),
      };

      console.log('📤 [ManagePartners] Payload:', JSON.stringify(payload, null, 2));

      const response = await apiClient.post<any>('/api/partners/register', payload);
      console.log('✅ [ManagePartners] API Response:', response.status, response.data);

      if (response.status === 201 || response.status === 200) {
        window.alert('Partner registered successfully!');
        setShowAddModal(false);
        resetForm();
        loadPartners();
      }
    } catch (error: any) {
      logger.error('❌ Failed to register partner', error);
      console.error('❌ [ManagePartners] Registration error:', error);
      const errorMsg = getErrorMessage(error, 'register partner');
      window.alert('Error: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePartner = async (partner: Partner) => {
    if (!window.confirm(`Are you sure you want to delete partner "${partner.name || partner.email}"?\n\nThis will also delete their user account.`)) {
      return;
    }

    try {
      setLoading(true);
      logger.info(`🗑️ Deleting partner via DELETE /api/partners/${partner.id}`);

      const response = await apiClient.delete<any>(`/api/partners/${partner.id}`);

      if (response.status === 200) {
        window.alert('Partner deleted successfully');
        setShowDetailModal(false);
        loadPartners();
      }
    } catch (error: any) {
      logger.error('❌ Failed to delete partner', error);
      const errorMsg = getErrorMessage(error, 'delete partner');
      window.alert('Error: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedPartner) return;

    // Validate
    const errors: Record<string, string> = {};
    if (!resetPasswordData.newPassword) {
      errors.newPassword = 'New password is required';
    } else if (resetPasswordData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }
    if (!resetPasswordData.confirmPassword) {
      errors.confirmPassword = 'Confirm password is required';
    } else if (resetPasswordData.newPassword !== resetPasswordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setResetPasswordErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const partnerId = selectedPartner.partnerId;
    if (!partnerId) {
      window.alert('Error: Partner ID not found');
      return;
    }

    try {
      setResettingPassword(true);
      logger.info(`🔑 Resetting password for partner: ${partnerId}`);

      const payload = {
        newPassword: resetPasswordData.newPassword,
        confirmPassword: resetPasswordData.confirmPassword,
      };

      const response = await apiClient.post<any>(`/api/auth/user/${partnerId}/reset-password`, payload);

      if (response.status === 200 || response.status === 201) {
        window.alert('Password reset successfully!');
        setShowResetPassword(false);
        setResetPasswordData({ newPassword: '', confirmPassword: '' });
        setResetPasswordErrors({});
      }
    } catch (error: any) {
      logger.error('❌ Failed to reset password', error);
      const errorMsg = getErrorMessage(error, 'reset password');
      window.alert('Error: ' + errorMsg);
    } finally {
      setResettingPassword(false);
    }
  };

  const viewPartnerDetail = async (partner: Partner) => {
    try {
      logger.info(`📋 Fetching partner detail: ${partner.id}`);
      const response = await apiClient.get<any>(`/api/partners/${partner.id}`);
      if (response.status === 200 && response.data) {
        const detail = response.data.data || response.data;
        setSelectedPartner(detail);
        setShowDetailModal(true);
      }
    } catch (error: any) {
      logger.error('Failed to load partner detail', error);
      // Fallback to list data
      setSelectedPartner(partner);
      setShowDetailModal(true);
    }
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      mobileCountryCode: '91',
      mobileNumber: '',
      password: '',
      confirmPassword: '',
      address: '',
      city: '',
      district: '',
      state: '',
      country: 'India',
      postalCode: '',
    });
    setFormErrors({});
  };

  const getDistrictsForState = (state: string): string[] => {
    return DISTRICTS_BY_STATE[state] || [];
  };

  const getErrorMessage = (error: any, action: string): string => {
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.response?.status === 409) {
      return 'A partner with this email or mobile already exists.';
    }
    if (error?.response?.status === 403) {
      return 'You do not have permission to perform this action.';
    }
    if (error?.response?.status === 400) {
      return error?.response?.data?.message || 'Invalid request. Please check your input.';
    }
    if (error?.response?.status === 404) {
      return 'Partner not found.';
    }
    return error?.message || `Failed to ${action}. Please try again.`;
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const renderFormField = (label: string, field: string, placeholder: string, options?: {
    keyboardType?: string;
    secureTextEntry?: boolean;
    multiline?: boolean;
  }) => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>{label}</Text>
      <TextInput
        style={[styles.input, formErrors[field] ? styles.inputError : null]}
        placeholder={placeholder}
        value={(formData as any)[field]}
        onChangeText={(text) => {
          setFormData({ ...formData, [field]: text });
          if (formErrors[field]) {
            setFormErrors({ ...formErrors, [field]: '' });
          }
        }}
        autoCapitalize={field === 'email' ? 'none' : 'sentences'}
        keyboardType={(options?.keyboardType as any) || 'default'}
        secureTextEntry={options?.secureTextEntry}
        multiline={options?.multiline}
      />
      {formErrors[field] ? <Text style={styles.errorText}>{formErrors[field]}</Text> : null}
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigate('/admin/dashboard')} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.title}>Manage Partners</Text>
        <View style={styles.headerRight}>
          <Text style={styles.partnerCount}>{partners.length} partner{partners.length !== 1 ? 's' : ''}</Text>
          <TouchableOpacity onPress={openAddModal} style={styles.addButton}>
            <Text style={styles.addButtonText}>+ Register Partner</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Search Bar */}
      <View style={styles.searchBar}>
        <TextInput
          style={styles.searchInput}
          placeholder="🔍 Search partners by name, email, mobile, city..."
          value={searchQuery}
          onChangeText={handleSearchChange}
          autoCapitalize="none"
        />
        {searchQuery ? (
          <TouchableOpacity
            onPress={() => { setSearchQuery(''); loadPartners(); }}
            style={styles.clearSearchButton}
          >
            <Text style={styles.clearSearchText}>✕</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      {/* Partners List */}
      <ScrollView style={styles.content}>
        {loading && partners.length === 0 ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#007AFF" />
            <Text style={styles.loadingText}>Loading partners...</Text>
          </View>
        ) : partners.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyIcon}>👥</Text>
            <Text style={styles.emptyText}>
              {searchQuery ? 'No partners match your search' : 'No partners found'}
            </Text>
            <Text style={styles.emptySubtext}>
              {searchQuery
                ? 'Try a different search term'
                : 'Click "+ Register Partner" to onboard your first partner'}
            </Text>
          </View>
        ) : (
          partners.map((partner) => (
            <TouchableOpacity
              key={partner.id}
              style={styles.partnerCard}
              onPress={() => viewPartnerDetail(partner)}
              activeOpacity={0.7}
            >
              <View style={styles.partnerCardLeft}>
                <View style={styles.avatarCircle}>
                  <Text style={styles.avatarText}>
                    {(partner.name || partner.email || '?')[0].toUpperCase()}
                  </Text>
                </View>
                <View style={styles.partnerInfo}>
                  <Text style={styles.partnerName}>{partner.name || 'Unnamed'}</Text>
                  {partner.partnerId ? (
                    <Text style={styles.partnerIdText}>ID: {partner.partnerId}</Text>
                  ) : null}
                  <Text style={styles.partnerEmail}>{partner.email}</Text>
                  <Text style={styles.partnerMobile}>
                    +{partner.mobileCountryCode || '91'} {partner.mobileNumber}
                  </Text>
                  {(partner.district || partner.state) ? (
                    <Text style={styles.partnerLocation}>
                      📍 {[partner.city, partner.district, partner.state].filter(Boolean).join(', ')}
                    </Text>
                  ) : null}
                </View>
              </View>
              <View style={styles.partnerCardRight}>
                <View style={[styles.statusBadge, (partner.status === 'ACTIVE' || partner.isActive !== false) ? styles.activeBadge : styles.inactiveBadge]}>
                  <Text style={[styles.statusBadgeText, (partner.status !== 'ACTIVE' && partner.isActive === false) && { color: '#991B1B' }]}>
                    {(partner.status === 'ACTIVE' || partner.isActive !== false) ? '● Active' : '○ Inactive'}
                  </Text>
                </View>
                {partner.createdAt ? (
                  <Text style={styles.partnerDate}>Joined {formatDate(partner.createdAt)}</Text>
                ) : null}
                <View style={styles.cardActions}>
                  <TouchableOpacity
                    onPress={(e: any) => { e.stopPropagation(); viewPartnerDetail(partner); }}
                    style={[styles.cardActionBtn, styles.viewBtn]}
                  >
                    <Text style={[styles.cardActionText, { color: '#2563EB' }]}>View</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={(e: any) => { e.stopPropagation(); handleDeletePartner(partner); }}
                    style={[styles.cardActionBtn, styles.deleteBtn]}
                  >
                    <Text style={[styles.cardActionText, { color: '#DC2626' }]}>Delete</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </TouchableOpacity>
          ))
        )}
      </ScrollView>

      {/* Add Partner Modal */}
      {showAddModal && (
        <View style={styles.modalOverlay}>
          <View style={styles.modal}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Register New Partner</Text>
              <TouchableOpacity onPress={() => setShowAddModal(false)}>
                <Text style={styles.modalCloseBtn}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
              {renderFormField('Full Name *', 'name', 'Enter partner full name')}
              {renderFormField('Email *', 'email', 'partner@example.com', { keyboardType: 'email-address' })}

              {/* Country Code */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Mobile Country Code *</Text>
                <View style={styles.dropdown}>
                  <select
                    value={formData.mobileCountryCode}
                    onChange={(e: any) => setFormData({ ...formData, mobileCountryCode: e.target.value })}
                    style={styles.select as any}
                  >
                    {countries.length > 0 ? countries.map((country) => (
                      <option key={country.id} value={country.mobileCountryCode}>
                        {country.countryName} (+{country.mobileCountryCode})
                      </option>
                    )) : (
                      <option value="91">India (+91)</option>
                    )}
                  </select>
                </View>
              </View>

              {renderFormField('Mobile Number *', 'mobileNumber', 'Enter mobile number', { keyboardType: 'phone-pad' })}
              {renderFormField('Password *', 'password', 'Minimum 6 characters', { secureTextEntry: true })}
              {renderFormField('Confirm Password *', 'confirmPassword', 'Re-enter password', { secureTextEntry: true })}

              <View style={styles.sectionDivider}>
                <Text style={styles.sectionTitle}>Address Information</Text>
              </View>

              {renderFormField('Address Line', 'address', 'Street address', { multiline: true })}
              {renderFormField('City', 'city', 'City name')}

              {/* State Dropdown */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>State *</Text>
                <View style={[styles.dropdown, formErrors.state ? styles.dropdownError : null]}>
                  <select
                    value={formData.state}
                    onChange={(e: any) => {
                      setFormData({ ...formData, state: e.target.value, district: '' });
                      if (formErrors.state) setFormErrors({ ...formErrors, state: '' });
                    }}
                    style={styles.select as any}
                  >
                    <option value="">Select State</option>
                    {INDIAN_STATES.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </View>
                {formErrors.state ? <Text style={styles.errorText}>{formErrors.state}</Text> : null}
              </View>

              {/* District Dropdown */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>District *</Text>
                <View style={[styles.dropdown, formErrors.district ? styles.dropdownError : null]}>
                  <select
                    value={formData.district}
                    onChange={(e: any) => {
                      setFormData({ ...formData, district: e.target.value });
                      if (formErrors.district) setFormErrors({ ...formErrors, district: '' });
                    }}
                    style={styles.select as any}
                    disabled={!formData.state}
                  >
                    <option value="">Select District</option>
                    {getDistrictsForState(formData.state).map((district) => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </View>
                {formErrors.district ? <Text style={styles.errorText}>{formErrors.district}</Text> : null}
              </View>

              {renderFormField('Postal Code', 'postalCode', 'PIN code', { keyboardType: 'number-pad' })}

            </ScrollView>
            {/* Action buttons outside ScrollView so they're always visible */}
            <View style={styles.modalFooter}>
              <TouchableOpacity
                onPress={() => setShowAddModal(false)}
                style={[styles.modalButton, styles.cancelButton]}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  console.log('🔵 [ManagePartners] Register button pressed');
                  handleAddPartner();
                }}
                style={[styles.modalButton, styles.submitButton]}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#FFF" />
                ) : (
                  <Text style={styles.submitButtonText}>Register Partner</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Partner Detail Modal */}
      {showDetailModal && selectedPartner && (
        <View style={styles.modalOverlay}>
          <View style={styles.modal}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Partner Details</Text>
              <TouchableOpacity onPress={() => setShowDetailModal(false)}>
                <Text style={styles.modalCloseBtn}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
              <View style={styles.detailSection}>
                <View style={styles.detailAvatarRow}>
                  <View style={styles.detailAvatar}>
                    <Text style={styles.detailAvatarText}>
                      {(selectedPartner.name || selectedPartner.email || '?')[0].toUpperCase()}
                    </Text>
                  </View>
                  <View style={{ flex: 1, marginLeft: 16 }}>
                    <Text style={styles.detailName}>{selectedPartner.name || 'Unnamed'}</Text>
                    {selectedPartner.partnerId ? (
                      <Text style={{ fontSize: 13, color: '#6B7280', fontWeight: '600', marginTop: 2 }}>
                        {selectedPartner.partnerId}
                      </Text>
                    ) : null}
                    <View style={[styles.statusBadge, selectedPartner.isActive !== false ? styles.activeBadge : styles.inactiveBadge, { alignSelf: 'flex-start', marginTop: 4 }]}>
                      <Text style={[styles.statusBadgeText, selectedPartner.isActive === false && { color: '#991B1B' }]}>
                        {selectedPartner.isActive !== false ? '● Active' : '○ Inactive'}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={styles.detailDivider} />

              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>Contact</Text>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Email</Text>
                  <Text style={styles.detailValue}>{selectedPartner.email}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Mobile</Text>
                  <Text style={styles.detailValue}>
                    +{selectedPartner.mobileCountryCode || '91'} {selectedPartner.mobileNumber}
                  </Text>
                </View>
              </View>

              <View style={styles.detailDivider} />

              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>Address</Text>
                {selectedPartner.address ? (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Address</Text>
                    <Text style={styles.detailValue}>{selectedPartner.address}</Text>
                  </View>
                ) : null}
                {selectedPartner.city ? (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>City</Text>
                    <Text style={styles.detailValue}>{selectedPartner.city}</Text>
                  </View>
                ) : null}
                {selectedPartner.district ? (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>District</Text>
                    <Text style={styles.detailValue}>{selectedPartner.district}</Text>
                  </View>
                ) : null}
                {selectedPartner.state ? (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>State</Text>
                    <Text style={styles.detailValue}>{selectedPartner.state}</Text>
                  </View>
                ) : null}
                {selectedPartner.country ? (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Country</Text>
                    <Text style={styles.detailValue}>{selectedPartner.country}</Text>
                  </View>
                ) : null}
              </View>

              <View style={styles.detailDivider} />

              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>System Info</Text>
                {selectedPartner.partnerId ? (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Partner ID</Text>
                    <Text style={[styles.detailValue, { fontWeight: '600' }]}>{selectedPartner.partnerId}</Text>
                  </View>
                ) : null}
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Internal ID</Text>
                  <Text style={[styles.detailValue, { fontSize: 12 }]}>{selectedPartner.id}</Text>
                </View>
                {selectedPartner.createdAt ? (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Registered</Text>
                    <Text style={styles.detailValue}>{formatDate(selectedPartner.createdAt)}</Text>
                  </View>
                ) : null}
              </View>

              <View style={styles.detailDivider} />

              {/* Reset Password Section */}
              <View style={styles.detailSection}>
                <TouchableOpacity
                  onPress={() => {
                    setShowResetPassword(!showResetPassword);
                    setResetPasswordData({ newPassword: '', confirmPassword: '' });
                    setResetPasswordErrors({});
                  }}
                  style={styles.resetPasswordToggle}
                >
                  <Text style={styles.detailSectionTitle}>🔑 Reset Password</Text>
                  <Text style={{ fontSize: 14, color: '#6B7280' }}>{showResetPassword ? '▲' : '▼'}</Text>
                </TouchableOpacity>

                {showResetPassword && (
                  <View style={styles.resetPasswordForm}>
                    <Text style={{ fontSize: 12, color: '#6B7280', marginBottom: 10 }}>
                      Reset password for: {selectedPartner.partnerId || selectedPartner.email}
                    </Text>
                    <View style={styles.inputGroup}>
                      <Text style={styles.label}>New Password *</Text>
                      <TextInput
                        style={[styles.input, resetPasswordErrors.newPassword ? styles.inputError : null]}
                        placeholder="Enter new password (min 6 chars)"
                        value={resetPasswordData.newPassword}
                        onChangeText={(text) => {
                          setResetPasswordData({ ...resetPasswordData, newPassword: text });
                          if (resetPasswordErrors.newPassword) {
                            setResetPasswordErrors({ ...resetPasswordErrors, newPassword: '' });
                          }
                        }}
                        secureTextEntry
                      />
                      {resetPasswordErrors.newPassword ? (
                        <Text style={styles.errorText}>{resetPasswordErrors.newPassword}</Text>
                      ) : null}
                    </View>
                    <View style={styles.inputGroup}>
                      <Text style={styles.label}>Confirm Password *</Text>
                      <TextInput
                        style={[styles.input, resetPasswordErrors.confirmPassword ? styles.inputError : null]}
                        placeholder="Confirm new password"
                        value={resetPasswordData.confirmPassword}
                        onChangeText={(text) => {
                          setResetPasswordData({ ...resetPasswordData, confirmPassword: text });
                          if (resetPasswordErrors.confirmPassword) {
                            setResetPasswordErrors({ ...resetPasswordErrors, confirmPassword: '' });
                          }
                        }}
                        secureTextEntry
                      />
                      {resetPasswordErrors.confirmPassword ? (
                        <Text style={styles.errorText}>{resetPasswordErrors.confirmPassword}</Text>
                      ) : null}
                    </View>
                    <TouchableOpacity
                      onPress={handleResetPassword}
                      style={[styles.resetPasswordBtn, resettingPassword && { opacity: 0.6 }]}
                      disabled={resettingPassword}
                    >
                      {resettingPassword ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                      ) : (
                        <Text style={styles.submitButtonText}>🔑 Reset Password</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                )}
              </View>

            </ScrollView>
            {/* Action buttons outside ScrollView */}
            <View style={styles.modalFooter}>
              <TouchableOpacity
                onPress={() => handleDeletePartner(selectedPartner)}
                style={[styles.modalButton, styles.deleteModalBtn]}
              >
                <Text style={styles.submitButtonText}>Delete Partner</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setShowDetailModal(false)}
                style={[styles.modalButton, styles.submitButton]}
              >
                <Text style={styles.submitButtonText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F7FA',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    paddingHorizontal: 20,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  backButton: {
    padding: 8,
  },
  backButtonText: {
    color: '#007AFF',
    fontSize: 16,
    fontWeight: '500',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1F2937',
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  partnerCount: {
    fontSize: 13,
    color: '#6B7280',
    fontWeight: '500',
  },
  addButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  addButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 14,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  searchInput: {
    flex: 1,
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    color: '#1F2937',
  },
  clearSearchButton: {
    marginLeft: 8,
    padding: 8,
    backgroundColor: '#E5E7EB',
    borderRadius: 6,
  },
  clearSearchText: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '600',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 60,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 15,
    color: '#6B7280',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 60,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 12,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#4B5563',
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  partnerCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  partnerCardLeft: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'flex-start',
  },
  avatarCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  avatarText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
  },
  partnerInfo: {
    flex: 1,
  },
  partnerName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
  },
  partnerIdText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: 2,
  },
  partnerEmail: {
    fontSize: 13,
    color: '#6B7280',
    marginBottom: 2,
  },
  partnerMobile: {
    fontSize: 13,
    color: '#6B7280',
    marginBottom: 2,
  },
  partnerLocation: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 2,
  },
  partnerCardRight: {
    alignItems: 'flex-end',
    marginLeft: 12,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 6,
  },
  activeBadge: {
    backgroundColor: '#D1FAE5',
  },
  inactiveBadge: {
    backgroundColor: '#FEE2E2',
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#065F46',
  },
  partnerDate: {
    fontSize: 11,
    color: '#9CA3AF',
    marginBottom: 8,
  },
  cardActions: {
    flexDirection: 'row',
    gap: 6,
  },
  cardActionBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  viewBtn: {
    backgroundColor: '#EFF6FF',
  },
  deleteBtn: {
    backgroundColor: '#FEF2F2',
  },
  cardActionText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
  },
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    width: '92%',
    maxWidth: 520,
    maxHeight: '85%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
  },
  modalCloseBtn: {
    fontSize: 20,
    color: '#9CA3AF',
    fontWeight: '600',
    padding: 4,
  },
  modalContent: {
    paddingHorizontal: 24,
    paddingVertical: 16,
    maxHeight: 500,
  },
  inputGroup: {
    marginBottom: 14,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    backgroundColor: '#FFFFFF',
    color: '#1F2937',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  errorText: {
    fontSize: 12,
    color: '#EF4444',
    marginTop: 4,
  },
  dropdown: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
  },
  dropdownError: {
    borderColor: '#EF4444',
  },
  select: {
    width: '100%',
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    border: 'none',
    backgroundColor: 'transparent',
    color: '#1F2937',
  },
  sectionDivider: {
    marginTop: 8,
    marginBottom: 12,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 20,
    paddingBottom: 8,
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
    borderBottomLeftRadius: 14,
    borderBottomRightRadius: 14,
  },
  modalButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    minWidth: 100,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#F3F4F6',
    borderWidth: 1,
    borderColor: '#D1D5DB',
  },
  cancelButtonText: {
    color: '#374151',
    fontWeight: '600',
    fontSize: 14,
  },
  submitButton: {
    backgroundColor: '#007AFF',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 14,
  },
  deleteModalBtn: {
    backgroundColor: '#EF4444',
  },
  detailSection: {
    paddingVertical: 8,
  },
  detailAvatarRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  detailAvatarText: {
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: '700',
  },
  detailName: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
  },
  detailDivider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: 8,
  },
  detailSectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: 6,
  },
  detailLabel: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
    width: 100,
  },
  detailValue: {
    fontSize: 14,
    color: '#1F2937',
    fontWeight: '500',
    flex: 1,
    textAlign: 'right',
  },
  resetPasswordToggle: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  resetPasswordForm: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
  },
  resetPasswordBtn: {
    backgroundColor: '#F59E0B',
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 8,
  },
});

